'use client';
import * as React from 'react';
import { useTheme } from 'next-themes';
import { cn } from '@/components/lib/utils';
import { Button as UIButton } from '@/components/ui/button';
import { Input as UIInput } from '@/components/ui/input';
import { Textarea as UITextarea } from '@/components/ui/textarea';
import { Badge as UIBadge } from '@/components/ui/badge';
import { Card as UICard, CardContent as UICardContent } from '@/components/ui/card';
import { Checkbox as UICheckbox } from '@/components/ui/checkbox';
import { Switch as UISwitch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Progress as UIProgress } from '@/components/ui/progress';
import { Avatar as UIAvatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton as UISkeleton } from '@/components/ui/skeleton';

type AnyProps = Record<string, any>;

const SPACE_TOKEN_TO_PX: Record<string, string> = {
  xs: '0.5rem',
  sm: '0.75rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

function toCssSpace(value: any, fallback = '1rem') {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${Math.max(0, value) * 0.25}rem`;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (SPACE_TOKEN_TO_PX[trimmed]) return SPACE_TOKEN_TO_PX[trimmed];
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return `${Math.max(0, Number(trimmed)) * 0.25}rem`;
    }
    return trimmed;
  }
  return fallback;
}

function spacingToClass(value: any, fallback = '4') {
  if (typeof value === 'number') return `gap-${Math.min(12, Math.max(0, value))}`;
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    if (trimmed === 'xs') return 'gap-2';
    if (trimmed === 'sm') return 'gap-3';
    if (trimmed === 'md') return 'gap-4';
    if (trimmed === 'lg') return 'gap-6';
    if (trimmed === 'xl') return 'gap-8';
    if (/^\d+$/.test(trimmed)) {
      return `gap-${Math.min(12, Math.max(0, Number(trimmed)))}`;
    }
    return trimmed;
  }
  return `gap-${fallback}`;
}

export function Box({ className, ...props }: AnyProps) {
  return <div className={cn(className)} {...props} />;
}

export function Container({ className, size, mt, mb, my, px, py, p, pos, ...props }: AnyProps) {
  const maxWidthMap: Record<string, string> = {
    xs: 'max-w-sm', sm: 'max-w-lg', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-7xl',
  };
  const maxW = typeof size === 'string' ? (maxWidthMap[size] ?? 'max-w-7xl') : (typeof size === 'number' ? '' : 'max-w-7xl');
  const style: React.CSSProperties = {};
  if (typeof size === 'number') style.maxWidth = size;
  const topPad = my ?? mt;
  const botPad = my ?? mb;
  const paddingTop = topPad === '10rem' || topPad === '12rem' ? 'pt-24' : topPad === '8rem' ? 'pt-20' : topPad === '6rem' ? 'pt-16' : topPad ? 'pt-10' : '';
  const paddingBot = botPad === '6rem' ? 'pb-16' : botPad === '4rem' ? 'pb-10' : botPad ? 'pb-10' : '';
  return <div className={cn('mx-auto px-4', maxW, paddingTop, paddingBot, className)} style={style} {...props} />;
}

export function Group({ className, gap, grow, justify, align, style, ...props }: AnyProps) {
  const groupStyle: React.CSSProperties = { ...(style || {}) };
  if (gap !== undefined) {
    groupStyle.gap = toCssSpace(gap, '1rem');
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center',
        spacingToClass(gap),
        justify === 'space-between' && 'justify-between',
        justify === 'center' && 'justify-center',
        justify === 'flex-end' && 'justify-end',
        align === 'start' && 'items-start',
        align === 'end' && 'items-end',
        grow && '[&>*]:flex-1',
        className,
      )}
      style={groupStyle}
      {...props}
    />
  );
}

export function Stack({ className, gap, style, ...props }: AnyProps) {
  const stackStyle: React.CSSProperties = { ...(style || {}) };
  if (gap !== undefined) {
    stackStyle.gap = toCssSpace(gap, '1rem');
  }
  return <div className={cn('flex flex-col', spacingToClass(gap), className)} style={stackStyle} {...props} />;
}

export function SimpleGrid({ className, cols = 2, spacing, ...props }: AnyProps) {
  return (
    <div
      className={cn('grid', spacingToClass(spacing), cols === 1 && 'grid-cols-1', cols === 2 && 'grid-cols-1 md:grid-cols-2', cols === 3 && 'grid-cols-1 md:grid-cols-3', cols >= 4 && 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4', className)}
      {...props}
    />
  );
}

export function Grid({ className, gutter, ...props }: AnyProps) {
  return <div className={cn('grid grid-cols-12', spacingToClass(gutter), className)} {...props} />;
}

export function GridCol({ className, span = 12, ...props }: AnyProps) {
  const safeSpan = typeof span === 'number' ? Math.min(12, Math.max(1, span)) : 12;
  return <div className={cn(`col-span-12 md:col-span-${safeSpan}`, className)} {...props} />;
}

export function Paper({ className, withBorder = true, p, px, py, pt, pb, pl, pr, m, mx, my, mt, mb, ml, mr, ta, radius, style, ...props }: AnyProps) {
  const paperStyle: React.CSSProperties = { ...(style || {}) };

  if (p !== undefined) paperStyle.padding = toCssSpace(p, '1rem');
  if (px !== undefined) {
    paperStyle.paddingLeft = toCssSpace(px, '1rem');
    paperStyle.paddingRight = toCssSpace(px, '1rem');
  }
  if (py !== undefined) {
    paperStyle.paddingTop = toCssSpace(py, '1rem');
    paperStyle.paddingBottom = toCssSpace(py, '1rem');
  }
  if (pt !== undefined) paperStyle.paddingTop = toCssSpace(pt, '1rem');
  if (pb !== undefined) paperStyle.paddingBottom = toCssSpace(pb, '1rem');
  if (pl !== undefined) paperStyle.paddingLeft = toCssSpace(pl, '1rem');
  if (pr !== undefined) paperStyle.paddingRight = toCssSpace(pr, '1rem');

  if (m !== undefined) paperStyle.margin = toCssSpace(m, '1rem');
  if (mx !== undefined) {
    paperStyle.marginLeft = toCssSpace(mx, '0');
    paperStyle.marginRight = toCssSpace(mx, '0');
  }
  if (my !== undefined) {
    paperStyle.marginTop = toCssSpace(my, '0');
    paperStyle.marginBottom = toCssSpace(my, '0');
  }
  if (mt !== undefined) paperStyle.marginTop = toCssSpace(mt, '0');
  if (mb !== undefined) paperStyle.marginBottom = toCssSpace(mb, '0');
  if (ml !== undefined) paperStyle.marginLeft = toCssSpace(ml, '0');
  if (mr !== undefined) paperStyle.marginRight = toCssSpace(mr, '0');

  if (ta === 'center') paperStyle.textAlign = 'center';
  if (ta === 'left') paperStyle.textAlign = 'left';
  if (ta === 'right') paperStyle.textAlign = 'right';

  if (radius === 'xs') paperStyle.borderRadius = '0.375rem';
  if (radius === 'sm') paperStyle.borderRadius = '0.5rem';
  if (radius === 'md') paperStyle.borderRadius = '0.625rem';
  if (radius === 'lg') paperStyle.borderRadius = '0.75rem';
  if (radius === 'xl') paperStyle.borderRadius = '1rem';

  return <UICard className={cn(withBorder && 'border', className)} style={paperStyle} {...props} />;
}

export function Card({ className, ...props }: AnyProps) {
  return <UICard className={cn(className)} {...props} />;
}

export function CardSection({ className, ...props }: AnyProps) {
  return <UICardContent className={cn(className)} {...props} />;
}

export function Text({ className, component, c, fw, size, ...props }: AnyProps) {
  const Tag = component || 'p';
  return <Tag className={cn(c === 'dimmed' && 'text-muted-foreground', fw === 700 && 'font-bold', size === 'sm' && 'text-sm', size === 'xs' && 'text-xs', className)} {...props} />;
}

export function Title({ className, order = 1, ...props }: AnyProps) {
  const map: Record<number, string> = { 1: 'text-4xl', 2: 'text-3xl', 3: 'text-2xl', 4: 'text-xl', 5: 'text-lg', 6: 'text-base' };
  const headingTags = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' } as const;
  const safeOrder = Math.min(6, Math.max(1, Number(order) || 1)) as 1 | 2 | 3 | 4 | 5 | 6;
  const Tag = headingTags[safeOrder];
  return <Tag className={cn('font-bold tracking-tight', map[order] || map[1], className)} {...props} />;
}

export function Button({ className, variant, color, leftSection, rightSection, loading, children, ...props }: AnyProps) {
  return (
    <UIButton
      className={cn(className)}
      variant={variant === 'outline' ? 'outline' : variant === 'subtle' ? 'ghost' : 'default'}
      {...props}
      disabled={loading || props.disabled}
    >
      {leftSection}
      {children}
      {rightSection}
    </UIButton>
  );
}

export const ActionIcon = React.forwardRef<HTMLButtonElement, AnyProps>(function ActionIcon({ className, loading, children, ...props }, ref) {
  return (
    <UIButton ref={ref} size="icon" variant="ghost" className={cn(className)} {...props} disabled={loading || props.disabled}>
      {children}
    </UIButton>
  );
});

export function Input({ className, component, children, pointer, rightSectionPointerEvents, ...props }: AnyProps) {
  if (component === 'button') {
    return (
      <button type="button" className={cn('inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm', className)} {...props}>
        {children}
      </button>
    );
  }

  return <UIInput className={cn(className)} {...props} />;
}

export function TextInput({ className, leftSection, rightSection, label, description, required, error, styles, ...props }: AnyProps) {
  const rootStyle = (styles && styles.root) || undefined;
  return (
    <div className={cn('flex flex-col gap-1', className)} style={rootStyle}>
      {label ? <label className="text-sm font-medium">{label}{required ? <span className="text-destructive ml-0.5">*</span> : null}</label> : null}
      <div className="relative">
        {leftSection ? <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{leftSection}</div> : null}
        <UIInput className={cn(leftSection && 'pl-10', rightSection && 'pr-10')} {...props} />
        {rightSection ? <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{rightSection}</div> : null}
      </div>
      {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </div>
  );
}

export function PasswordInput(props: AnyProps) {
  return <TextInput type="password" {...props} />;
}

export function NumberInput({ value, onChange, label, description, required, error, className, styles, ...props }: AnyProps) {
  const rootStyle = (styles && styles.root) || undefined;
  return (
    <div className={cn('flex flex-col gap-1', className)} style={rootStyle}>
      {label ? <label className="text-sm font-medium">{label}{required ? <span className="text-destructive ml-0.5">*</span> : null}</label> : null}
      <UIInput
        type="number"
        value={value ?? ''}
        onChange={(event) => onChange?.(event.target.value === '' ? '' : Number(event.target.value))}
        {...props}
      />
      {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </div>
  );
}

export function Textarea({ className, label, description, required, error, styles, ...props }: AnyProps) {
  const rootStyle = (styles && styles.root) || undefined;
  if (!label && !description && !error) {
    return <UITextarea className={cn(className)} {...props} />;
  }
  return (
    <div className={cn('flex flex-col gap-1', className)} style={rootStyle}>
      {label ? <label className="text-sm font-medium">{label}{required ? <span className="text-destructive ml-0.5">*</span> : null}</label> : null}
      <UITextarea {...props} />
      {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </div>
  );
}

export function Checkbox({ checked, onChange, label, description, className, ...props }: AnyProps) {
  const handleChange = (v: boolean) => {
    const evt = { target: { checked: !!v }, currentTarget: { checked: !!v } } as any;
    onChange?.(evt);
  };
  return (
    <label className={cn('inline-flex items-start gap-2', className)}>
      <UICheckbox checked={!!checked} onCheckedChange={handleChange} {...props} />
      {(label || description) ? (
        <span className="flex flex-col">
          {label ? <span className="text-sm">{label}</span> : null}
          {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
}

export function Switch({ checked, onChange, label, description, className, ...props }: AnyProps) {
  const handleChange = (v: boolean) => {
    const evt = { target: { checked: !!v }, currentTarget: { checked: !!v } } as any;
    onChange?.(evt);
  };
  return (
    <label className={cn('inline-flex items-start gap-2', className)}>
      <UISwitch checked={!!checked} onCheckedChange={handleChange} {...props} />
      {(label || description) ? (
        <span className="flex flex-col">
          {label ? <span className="text-sm">{label}</span> : null}
          {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
}

export function Select({ data = [], value, onChange, className, label, description, required, error, styles, ...props }: AnyProps) {
  const rootStyle = (styles && styles.root) || undefined;
  const select = (
    <select className={cn('flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm', !label && !description && className)} value={value ?? ''} onChange={(e) => onChange?.(e.target.value)} {...props}>
      {(data || []).map((item: any) => {
        const option = typeof item === 'string' ? { value: item, label: item } : item;
        return <option key={option.value} value={option.value}>{option.label}</option>;
      })}
    </select>
  );
  if (!label && !description && !error) return select;
  return (
    <div className={cn('flex flex-col gap-1', className)} style={rootStyle}>
      {label ? <label className="text-sm font-medium">{label}{required ? <span className="text-destructive ml-0.5">*</span> : null}</label> : null}
      {select}
      {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </div>
  );
}

export function MultiSelect({ data = [], value = [], onChange, className, ...props }: AnyProps) {
  return (
    <select
      multiple
      className={cn('flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm', className)}
      value={value}
      onChange={(e) => onChange?.(Array.from(e.target.selectedOptions).map((opt) => opt.value))}
      {...props}
    >
      {(data || []).map((item: any) => {
        const option = typeof item === 'string' ? { value: item, label: item } : item;
        return <option key={option.value} value={option.value}>{option.label}</option>;
      })}
    </select>
  );
}

export function TagsInput({ value = [], onChange, className, ...props }: AnyProps) {
  return (
    <UIInput
      className={cn(className)}
      value={(value || []).join(', ')}
      onChange={(event) => onChange?.(event.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
      {...props}
    />
  );
}

export function Badge({ className, variant, children, ...props }: AnyProps) {
  return <UIBadge className={cn(className)} variant={variant === 'outline' ? 'outline' : 'default'} {...props}>{children}</UIBadge>;
}

export function Divider({ className, ...props }: AnyProps) {
  return <Separator className={cn(className)} {...props} />;
}

export function Alert({ className, title, children, color, ...props }: AnyProps) {
  return (
    <div className={cn('rounded-lg border p-3', color === 'red' && 'border-destructive/30 bg-destructive/10', className)} {...props}>
      {title ? <p className="mb-1 font-semibold">{title}</p> : null}
      <div>{children}</div>
    </div>
  );
}

export function Loader({ className }: AnyProps) {
  return <div className={cn('h-5 w-5 animate-spin rounded-full border-2 border-current border-r-transparent', className)} />;
}

export function LoadingOverlay({ visible }: AnyProps) {
  if (!visible) return null;
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70">
      <Loader />
    </div>
  );
}

export function Modal({ opened, onClose, title, children, className }: AnyProps) {
  if (!opened) return null;
  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className={cn('w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border bg-background p-4 shadow-xl', className)} onClick={(e) => e.stopPropagation()}>
        {title ? <h3 className="mb-3 text-lg font-semibold">{title}</h3> : null}
        {children}
      </div>
    </div>
  );
}

const TabsContext = React.createContext<{ value: string; onChange: (v: string) => void }>({ value: '', onChange: () => {} });

export function Tabs({ value, onChange, children, className }: AnyProps) {
  return (
    <TabsContext.Provider value={{ value: value || '', onChange: onChange || (() => {}) }}>
      <div className={cn(className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, ...props }: AnyProps) {
  return <div className={cn('mb-4 flex flex-wrap gap-2', className)} {...props} />;
}

export function TabsTab({ value, className, children, leftSection, ...props }: AnyProps) {
  const ctx = React.useContext(TabsContext);
  const isActive = ctx.value === value;
  return (
    <button
      type="button"
      data-tab-value={value}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm transition-colors',
        isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-transparent text-muted-foreground hover:bg-accent',
        className
      )}
      onClick={() => ctx.onChange(value)}
      {...props}
    >
      {leftSection && <span className="mr-2 inline-flex">{leftSection}</span>}
      {children}
    </button>
  );
}

export function TabsPanel({ value, className, children, p, px, py, pt, pb, pl, pr, m, mt, mb, mx, my, style, ...props }: AnyProps) {
  const ctx = React.useContext(TabsContext);
  if (ctx.value !== value) return null;

  const panelStyle: React.CSSProperties = { ...(style || {}) };
  if (p !== undefined) panelStyle.padding = toCssSpace(p, '1rem');
  if (px !== undefined) {
    panelStyle.paddingLeft = toCssSpace(px, '1rem');
    panelStyle.paddingRight = toCssSpace(px, '1rem');
  }
  if (py !== undefined) {
    panelStyle.paddingTop = toCssSpace(py, '1rem');
    panelStyle.paddingBottom = toCssSpace(py, '1rem');
  }
  if (pt !== undefined) panelStyle.paddingTop = toCssSpace(pt, '1rem');
  if (pb !== undefined) panelStyle.paddingBottom = toCssSpace(pb, '1rem');
  if (pl !== undefined) panelStyle.paddingLeft = toCssSpace(pl, '1rem');
  if (pr !== undefined) panelStyle.paddingRight = toCssSpace(pr, '1rem');

  if (m !== undefined) panelStyle.margin = toCssSpace(m, '0');
  if (mx !== undefined) {
    panelStyle.marginLeft = toCssSpace(mx, '0');
    panelStyle.marginRight = toCssSpace(mx, '0');
  }
  if (my !== undefined) {
    panelStyle.marginTop = toCssSpace(my, '0');
    panelStyle.marginBottom = toCssSpace(my, '0');
  }
  if (mt !== undefined) panelStyle.marginTop = toCssSpace(mt, '0');
  if (mb !== undefined) panelStyle.marginBottom = toCssSpace(mb, '0');

  return <div className={cn(className)} style={panelStyle} {...props}>{children}</div>;
}

(Tabs as any).List = TabsList;
(Tabs as any).Tab = TabsTab;
(Tabs as any).Panel = TabsPanel;

export function Accordion({ children, className }: AnyProps) {
  return <div className={cn('space-y-2', className)}>{children}</div>;
}

export function AccordionItem({ children, className }: AnyProps) {
  return <div className={cn('rounded-md border', className)}>{children}</div>;
}

export function AccordionControl({ children, className, ...props }: AnyProps) {
  return <button type="button" className={cn('w-full px-4 py-3 text-left font-medium', className)} {...props}>{children}</button>;
}

export function AccordionPanel({ children, className }: AnyProps) {
  return <div className={cn('px-4 pb-4', className)}>{children}</div>;
}

(Accordion as any).Item = AccordionItem;
(Accordion as any).Control = AccordionControl;
(Accordion as any).Panel = AccordionPanel;

export function Tooltip({ children }: AnyProps) {
  return <>{children}</>;
}

export function ScrollArea({ className, ...props }: AnyProps) {
  return <div className={cn('overflow-auto', className)} {...props} />;
}

export function Pagination({ total = 1, value = 1, onChange, className }: AnyProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <UIButton variant="outline" size="sm" onClick={() => onChange?.(Math.max(1, value - 1))} disabled={value <= 1}>Prev</UIButton>
      <span className="text-sm text-muted-foreground">{value} / {total}</span>
      <UIButton variant="outline" size="sm" onClick={() => onChange?.(Math.min(total, value + 1))} disabled={value >= total}>Next</UIButton>
    </div>
  );
}

export function Table({ className, ...props }: AnyProps) {
  return <table className={cn('w-full text-sm', className)} {...props} />;
}

export function Code({ className, ...props }: AnyProps) {
  return <code className={cn('rounded bg-muted px-1.5 py-0.5', className)} {...props} />;
}

export function Progress({ value = 0, className }: AnyProps) {
  return <UIProgress value={value} className={cn(className)} />;
}

export function Avatar({ src, children, className, ...props }: AnyProps) {
  return (
    <UIAvatar className={cn(className)} {...props}>
      <AvatarImage src={src} />
      <AvatarFallback>{children}</AvatarFallback>
    </UIAvatar>
  );
}

export function Skeleton({ className, circle, ...props }: AnyProps) {
  return <UISkeleton className={cn(circle && 'rounded-full', className)} {...props} />;
}

export function Image({ className, alt = '', lightHidden, darkHidden, ...props }: AnyProps) {
  return <img className={cn(lightHidden && 'dark:hidden', darkHidden && 'hidden dark:block', className)} alt={alt} {...props} />;
}

export function Anchor({ className, ...props }: AnyProps) {
  return <a className={cn('text-primary hover:underline', className)} {...props} />;
}

export function Center({ className, ...props }: AnyProps) {
  return <div className={cn('flex items-center justify-center', className)} {...props} />;
}

export function List({ className, ...props }: AnyProps) {
  return <ul className={cn('list-disc pl-6', className)} {...props} />;
}

export function ListItem({ className, ...props }: AnyProps) {
  return <li className={cn(className)} {...props} />;
}

export function Slider({ value = 0, onChange, className, ...props }: AnyProps) {
  return <UIInput type="range" className={cn(className)} value={value} onChange={(e) => onChange?.(Number(e.target.value))} {...props} />;
}

export function SegmentedControl({ data = [], value, onChange, className }: AnyProps) {
  return (
    <div className={cn('inline-flex rounded-md border p-1', className)}>
      {data.map((item: any) => {
        const option = typeof item === 'string' ? { value: item, label: item } : item;
        const active = option.value === value;
        return (
          <UIButton key={option.value} size="sm" variant={active ? 'default' : 'ghost'} onClick={() => onChange?.(option.value)}>
            {option.label}
          </UIButton>
        );
      })}
    </div>
  );
}

export function Radio({ checked, onChange, label, value, className }: AnyProps) {
  return (
    <label className={cn('inline-flex items-center gap-2', className)}>
      <input type="radio" checked={!!checked} value={value} onChange={(e) => onChange?.(e)} />
      <span>{label}</span>
    </label>
  );
}

export function CopyButton({ value, children }: AnyProps) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(String(value ?? ''));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  return children?.({ copied, copy });
}

export function ColorSwatch({ color = '#fff', className }: AnyProps) {
  return <span className={cn('inline-block h-4 w-4 rounded-full border', className)} style={{ backgroundColor: color }} />;
}

export function Notification({ title, children, className }: AnyProps) {
  return (
    <div className={cn('rounded-md border p-3', className)}>
      {title ? <p className="font-semibold">{title}</p> : null}
      <div>{children}</div>
    </div>
  );
}

export function Breadcrumbs({ children, className }: AnyProps) {
  return <nav className={cn('flex flex-wrap items-center gap-2 text-sm', className)}>{children}</nav>;
}

export function Menu({ children }: AnyProps) {
  return <div>{children}</div>;
}

export function MenuTarget({ children }: AnyProps) {
  return <>{children}</>;
}

export function MenuDropdown({ children, className }: AnyProps) {
  return <div className={cn('rounded-md border bg-background p-1', className)}>{children}</div>;
}

export function MenuItem({ children, className, ...props }: AnyProps) {
  return <button type="button" className={cn('block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent', className)} {...props}>{children}</button>;
}

(Menu as any).Target = MenuTarget;
(Menu as any).Dropdown = MenuDropdown;
(Menu as any).Item = MenuItem;

export function Popover({ children }: AnyProps) {
  return <div>{children}</div>;
}

export function PopoverTarget({ children }: AnyProps) {
  return <>{children}</>;
}

export function PopoverDropdown({ children, className }: AnyProps) {
  return <div className={cn('rounded-md border bg-background p-3', className)}>{children}</div>;
}

(Popover as any).Target = PopoverTarget;
(Popover as any).Dropdown = PopoverDropdown;

export function FileButton({ children, onChange, accept, multiple }: AnyProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        className="hidden"
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          const files = event.target.files;
          onChange?.(multiple ? Array.from(files || []) : files?.[0] ?? null);
        }}
      />
      {children?.({ onClick: () => inputRef.current?.click() })}
    </>
  );
}

export function FileInput({ value, onChange, className, ...props }: AnyProps) {
  return (
    <input
      type="file"
      className={cn('block w-full text-sm', className)}
      onChange={(event) => onChange?.(event.target.files?.[0] ?? null)}
      {...props}
    />
  );
}

export function UnstyledButton({ className, ...props }: AnyProps) {
  return <button type="button" className={cn(className)} {...props} />;
}

export function CloseButton({ className, ...props }: AnyProps) {
  return <UIButton size="icon" variant="ghost" className={cn(className)} {...props}>x</UIButton>;
}

export function Burger({ opened, onClick, className }: AnyProps) {
  return (
    <UIButton type="button" size="icon" variant="ghost" onClick={onClick} className={cn(className)}>
      <span>{opened ? 'x' : '='}</span>
    </UIButton>
  );
}

export function Transition({ mounted, children }: AnyProps) {
  if (!mounted) return null;
  return typeof children === 'function' ? children({}) : children;
}

function ComboboxRoot({ children }: AnyProps) {
  return <div>{children}</div>;
}

function ComboboxTarget({ children }: AnyProps) {
  return <>{children}</>;
}

function ComboboxDropdown({ children, className, ...props }: AnyProps) {
  return <div className={cn('mt-2 rounded-md border bg-popover p-1 text-popover-foreground shadow-md', className)} {...props}>{children}</div>;
}

function ComboboxOptions({ children, className, ...props }: AnyProps) {
  return <div className={cn('flex flex-col gap-1', className)} {...props}>{children}</div>;
}

function ComboboxOption({ children, className, value, ...props }: AnyProps) {
  return (
    <button type="button" className={cn('w-full rounded-sm px-2 py-1 text-left text-sm hover:bg-accent', className)} data-value={value} {...props}>
      {children}
    </button>
  );
}

export const Combobox = Object.assign(ComboboxRoot, {
  Target: ComboboxTarget,
  Dropdown: ComboboxDropdown,
  Options: ComboboxOptions,
  Option: ComboboxOption,
});

export function useCombobox(_options?: AnyProps) {
  return {
    opened: false,
    openDropdown: () => undefined,
    closeDropdown: () => undefined,
    toggleDropdown: () => undefined,
    resetSelectedOption: () => undefined,
    selectOption: () => undefined,
  };
}

export function useColorScheme() {
  const { theme, setTheme } = useTheme();
  const colorScheme = theme === 'light' ? 'light' : 'dark';
  return {
    colorScheme,
    setColorScheme: (next: 'light' | 'dark') => setTheme(next),
    toggleColorScheme: () => setTheme(colorScheme === 'dark' ? 'light' : 'dark'),
  };
}

export type ColorScheme = 'light' | 'dark';

